import { InventoryItem, KpiSummary } from '../types/inventory';

export interface InventoryDataResponse {
  items: InventoryItem[];
  kpis: KpiSummary;
  selectOrders: Record<string, string[]>;
  lastSyncDisplay: string;
}

// Persistencia de Ajustes Recientes en Almacenamiento Local (Fase 9H - Decisión D3)
const STOCK_OVERRIDES_KEY = 'sanesca_stock_overrides';
const COST_OVERRIDES_KEY = 'sanesca_cost_overrides';

export function saveStockOverride(dashboardId: string, newStock: number, nuevoEstadoStock?: string) {
  try {
    if (typeof window === 'undefined') return;
    const raw = localStorage.getItem(STOCK_OVERRIDES_KEY);
    const overrides = raw ? JSON.parse(raw) : {};
    overrides[dashboardId] = {
      stock: newStock,
      estadoStock: nuevoEstadoStock,
      timestamp: Date.now()
    };
    localStorage.setItem(STOCK_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch (e) {
    console.warn('Error guardando override de stock:', e);
  }
}

export function saveCostOverride(dashboardId: string, newCost: number) {
  try {
    if (typeof window === 'undefined') return;
    if (!newCost || newCost <= 0) return;
    const raw = localStorage.getItem(COST_OVERRIDES_KEY);
    const overrides = raw ? JSON.parse(raw) : {};
    overrides[dashboardId] = {
      cost: newCost,
      timestamp: Date.now()
    };
    localStorage.setItem(COST_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch (e) {
    console.warn('Error guardando override de costo:', e);
  }
}

export function removeStockOverride(dashboardId: string) {
  try {
    if (typeof window === 'undefined') return;
    const raw = localStorage.getItem(STOCK_OVERRIDES_KEY);
    if (!raw) return;
    const overrides = JSON.parse(raw);
    delete overrides[dashboardId];
    localStorage.setItem(STOCK_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch (e) {
    console.warn('Error eliminando override de stock:', e);
  }
}

export function getStockOverrides(): Record<string, { stock: number; estadoStock?: string; timestamp: number }> {
  try {
    if (typeof window === 'undefined') return {};
    const raw = localStorage.getItem(STOCK_OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const now = Date.now();
    const clean: Record<string, { stock: number; estadoStock?: string; timestamp: number }> = {};
    // Mantener overrides de las últimas 48 horas
    for (const [id, data] of Object.entries(parsed)) {
      const d = data as any;
      if (now - d.timestamp < 48 * 3600 * 1000) {
        clean[id] = d;
      }
    }
    return clean;
  } catch {
    return {};
  }
}

export function getCostOverrides(): Record<string, { cost: number; timestamp: number }> {
  try {
    if (typeof window === 'undefined') return {};
    const raw = localStorage.getItem(COST_OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const now = Date.now();
    const clean: Record<string, { cost: number; timestamp: number }> = {};
    for (const [id, data] of Object.entries(parsed)) {
      const d = data as any;
      if (now - d.timestamp < 48 * 3600 * 1000) {
        clean[id] = d;
      }
    }
    return clean;
  } catch {
    return {};
  }
}

export async function loadInventoryData(): Promise<InventoryDataResponse> {
  const ts = Date.now();
  let items: InventoryItem[] = [];
  let kpis: KpiSummary = {
    total: 0,
    estado: { sinStock: 0, bajoMinimo: 0, enStock: 0, enReconteo: 0, descontinuado: 0 },
    prioridad: { urgente: 0, alta: 0, media: 0, baja: 0, porPedido: 0 },
    auditados3D: 0,
    auditados3DPct: 0,
  };
  let selectOrders: Record<string, string[]> = {};
  let lastSyncDisplay = '—';

  try {
    const [invRes, metaRes, catalogRes] = await Promise.all([
      fetch(`data/inventory.json?t=${ts}`).catch(() => null),
      fetch(`data/meta.json?t=${ts}`).catch(() => null),
      fetch(`/api/catalog/items?t=${ts}`).catch(() => null),
    ]);

    if (invRes && invRes.ok) {
      const invData = await invRes.json();
      items = invData.items || [];
      if (invData.kpis) kpis = invData.kpis;
      if (invData.selectOrders) selectOrders = invData.selectOrders;
    }

    if (metaRes && metaRes.ok) {
      const metaData = await metaRes.json();
      if (metaData.lastSyncLocal) {
        lastSyncDisplay = metaData.lastSyncLocal;
      } else if (metaData.lastSync) {
        lastSyncDisplay = new Date(metaData.lastSync).toLocaleString('es-VE');
      }
    }

    // Hidratación Dinámica en Edge (PLAN-013 / Decisión 1 - Ruta A):
    // Inyectar insumos de Notion no presentes en el snapshot estático (441+ items)
    if (catalogRes && catalogRes.ok) {
      try {
        const catData = await catalogRes.json();
        const catItems = Array.isArray(catData.items) ? catData.items : [];
        if (catItems.length > 0) {
          const itemKeyMap = new Map<string, InventoryItem>();
          items.forEach(it => {
            if (it.id) itemKeyMap.set(it.id, it);
            if (it.insumoId) itemKeyMap.set(it.insumoId, it);
            if (it.codigo) itemKeyMap.set(it.codigo.toUpperCase().trim(), it);
            if (it.nombre) itemKeyMap.set(it.nombre.toLowerCase().trim(), it);
          });

          for (const cItem of catItems) {
            const cIsStorable = cItem.isStorable !== false && cItem.contando !== false;
            const cSku = (cItem.codigo || '').toUpperCase().trim();
            const cNom = (cItem.nombre || '').toLowerCase().trim();
            const match = (cItem.id && itemKeyMap.get(cItem.id)) ||
                          (cItem.insumoId && itemKeyMap.get(cItem.insumoId)) ||
                          (cSku && itemKeyMap.get(cSku)) ||
                          (cNom && itemKeyMap.get(cNom));

            if (match) {
              if (!cIsStorable) {
                // Desmarcado en Notion: excluir del tablero operativo
                match.isStorable = false;
                match.contando = false;
                continue;
              }
              match.isStorable = true;
              match.contando = true;
              if (cItem.stockBase !== undefined && cItem.stockBase !== null) {
                if (match.stockBase === 0 && cItem.stockBase > 0) {
                  match.stockBase = cItem.stockBase;
                }
              }
              if (cItem.costoUnitarioUSD && (!match.costoUnitarioUSD || match.costoUnitarioUSD <= 0)) {
                match.costoUnitarioUSD = cItem.costoUnitarioUSD;
              }
              if (cItem.codigo && !match.codigo) match.codigo = cItem.codigo;
              if (cItem.insumoId && !match.insumoId) match.insumoId = cItem.insumoId;
              if (cItem.dashboardId && !match.dashboardId) match.dashboardId = cItem.dashboardId;
              if (cItem.conceptoId) match.conceptoId = cItem.conceptoId;
              if (cItem.largo !== undefined && cItem.largo !== null) match.largo = cItem.largo;
              if (cItem.ancho !== undefined && cItem.ancho !== null) match.ancho = cItem.ancho;
              if (cItem.espesor !== undefined && cItem.espesor !== null) match.espesor = cItem.espesor;

              // Dualidad Canónica y Sincronización en Vivo de Stock Mínimo (PLAN-031)
              if (cItem.nombreISO) {
                match.nombreISO = cItem.nombreISO;
              }
              if (cItem.nombre && cItem.nombre !== 'Insumo sin Nombre') {
                match.nombre = cItem.nombre;
              }
              if (typeof cItem.stockMinimo === 'number' && cItem.stockMinimo >= 0) {
                match.stockMinimo = cItem.stockMinimo;
                match.deficit = Math.max(0, match.stockMinimo - match.stockBase);
                if (match.stockBase <= 0) {
                  match.estadoStock = 'Sin Stock';
                  match.estadoStockColor = 'red';
                } else if (match.stockMinimo > 0 && match.stockBase < match.stockMinimo) {
                  match.estadoStock = 'Bajo Mínimo';
                  match.estadoStockColor = 'orange';
                } else {
                  match.estadoStock = 'En Stock';
                  match.estadoStockColor = 'green';
                }
              }
            } else if (cIsStorable && cItem.nombre && cItem.nombre !== 'Insumo sin Nombre') {
              // Odoo 18 domain: solo inyectar si es almacenable
              const baseStk = cItem.stockBase || 0;
              const minStk = cItem.stockMinimo || 0;
              const def = Math.max(0, minStk - baseStk);
              const estStk = cItem.estadoStock || (baseStk === 0 ? 'Sin Stock' : (baseStk < minStk ? 'Bajo Mínimo' : 'En Stock'));

              const mapped: InventoryItem = {
                id: cItem.dashboardId || cItem.id,
                insumoId: cItem.insumoId || cItem.id,
                dashboardId: cItem.dashboardId || cItem.id,
                nombre: cItem.nombre,
                nombreISO: cItem.nombreISO || cItem.nombre,
                codigo: cItem.codigo || '',
                conceptoId: cItem.conceptoId || null,
                largo: cItem.largo ?? null,
                ancho: cItem.ancho ?? null,
                espesor: cItem.espesor ?? null,
                marca: cItem.marca || '',
                categoriaMaterial: cItem.categoria || 'General',
                rolMaterial: cItem.rolMaterial || 'Materia Prima',
                unidad: cItem.unidad || 'UND',
                stockBase: baseStk,
                stockMinimo: minStk,
                deficit: def,
                estadoStock: estStk,
                estadoStockColor: estStk === 'Sin Stock' ? 'red' : (estStk === 'Bajo Mínimo' ? 'orange' : 'green'),
                prioridad: baseStk < minStk ? 'Alta' : 'Media',
                prioridadColor: 'orange',
                costoUnitarioUSD: cItem.costoUnitarioUSD || 0,
                color: cItem.color || '',
                dimensiones: cItem.dimensiones || '',
                enTransitoOAB: 0,
                stockProyectado: baseStk,
                stockApartado: 0,
                stockLibre: baseStk,
                contando: true,
                isStorable: true,
                ubicacion: cItem.ubicacion || '',
                isOptimisticSync: false
              };
              items.push(mapped);
              if (mapped.id) itemKeyMap.set(mapped.id, mapped);
              if (mapped.insumoId) itemKeyMap.set(mapped.insumoId, mapped);
              if (cSku) itemKeyMap.set(cSku, mapped);
              if (cNom) itemKeyMap.set(cNom, mapped);
            }
          }
          // Filtro de dominio canónico: la vista principal solo expone insumos almacenables
          items = items.filter(it => it.isStorable !== false && it.contando !== false);
        }
      } catch (catErr) {
        console.warn('Error procesando catálogo maestro en loadInventoryData:', catErr);
      }
    }
  } catch (err) {
    console.warn('Fallo cargando data estática local:', err);
  }

  // Actualizar KPI total con la cantidad real de ítems
  kpis.total = items.length;

  // Leer sobreescrituras locales recientes de ajustes físicos y costos (Fase 9H/9I - Decisión D5)
  const overrides = typeof window !== 'undefined' ? getStockOverrides() : {};
  const costOverrides = typeof window !== 'undefined' ? getCostOverrides() : {};
  let overridesModified = false;
  let costOverridesModified = false;

  // Enriquecer items con enTransitoOAB, stockProyectado y reconciliar/evictar overrides locales
  items = items.map(item => {
    const override = overrides[item.id];
    let baseStock = item.stockBase || 0;
    let estado = item.estadoStock;

    if (override !== undefined) {
      // Reconciliación Fase 9I: si el JSON estático ya consolidó el saldo ajustado, evictar override
      if (item.stockBase === override.stock) {
        delete overrides[item.id];
        overridesModified = true;
      } else {
        // Discrepancia activa: prevalece el override reciente del usuario
        baseStock = override.stock;
        if (override.estadoStock) estado = override.estadoStock;
      }
    }

    // Reconciliación reactiva de Costo Unitario
    const costOverride = costOverrides[item.id];
    let unitCost = item.costoUnitarioUSD || 0;
    if (costOverride !== undefined && costOverride.cost > 0) {
      if (item.costoUnitarioUSD === costOverride.cost) {
        delete costOverrides[item.id];
        costOverridesModified = true;
      } else {
        unitCost = costOverride.cost;
      }
    }

    const enTransito = item.enTransitoOAB || 0;
    const proyectado = baseStock + enTransito;
    const deficit = Math.max(0, (item.stockMinimo || 0) - baseStock);
    return {
      ...item,
      stockBase: baseStock,
      deficit,
      estadoStock: estado,
      enTransitoOAB: enTransito,
      stockProyectado: proyectado,
      costoUnitarioUSD: unitCost
    };
  });

  // Si se desalojaron overrides reconciliados, sincronizar localStorage
  if (overridesModified && typeof window !== 'undefined') {
    try {
      localStorage.setItem(STOCK_OVERRIDES_KEY, JSON.stringify(overrides));
    } catch (e) {
      console.warn('Error persistiendo evicción de stock overrides:', e);
    }
  }

  if (costOverridesModified && typeof window !== 'undefined') {
    try {
      localStorage.setItem(COST_OVERRIDES_KEY, JSON.stringify(costOverrides));
    } catch (e) {
      console.warn('Error persistiendo evicción de cost overrides:', e);
    }
  }

  // Siempre computar KPIs reales basados en los insumos almacenables activos en piso
  if (items.length > 0) {
    const sinStock = items.filter(i => (i.stockBase || 0) === 0).length;
    const bajoMinimo = items.filter(i => (i.stockBase || 0) > 0 && (i.stockBase || 0) < (i.stockMinimo || 0)).length;
    const enStock = items.filter(i => (i.stockBase || 0) >= (i.stockMinimo || 0) && (i.stockBase || 0) > 0).length;
    const reconteo3D = items.filter(i => i.seReconto3D).length;
    kpis = {
      ...kpis,
      total: items.length,
      estado: {
        ...kpis.estado,
        sinStock,
        bajoMinimo,
        enStock
      },
      auditados3D: reconteo3D,
      auditados3DPct: Math.round((reconteo3D / items.length) * 100)
    };
  }

  return {
    items,
    kpis,
    selectOrders,
    lastSyncDisplay,
  };
}

export interface BCVRateResponse {
  rate: number;
  source: string;
  status: 'success' | 'stale_cache' | 'fallback';
  updatedAt?: string;
}

export async function fetchBCVRate(): Promise<BCVRateResponse> {
  try {
    const res = await fetch('/api/bcv/rate');
    if (res.ok) {
      const data = await res.json();
      if (data.promedio && Number(data.promedio) > 0) {
        return {
          rate: Number(data.promedio),
          source: data.fuente || 'BCV Oficial',
          status: data.status || 'success',
          updatedAt: data.fechaActualizacion
        };
      }
    }
  } catch (err) {
    console.warn('No se pudo consultar /api/bcv/rate, usando fallback referencial:', err);
  }

  return {
    rate: 880.00,
    source: 'Referencial (Contingencia Extrema)',
    status: 'fallback',
    updatedAt: new Date().toISOString()
  };
}

export interface RevalidationResult {
  updatedItems: InventoryItem[];
  activeOrdersCount: number;
  synced: boolean;
}

export async function revalidateInventoryLive(currentItems: InventoryItem[]): Promise<RevalidationResult> {
  try {
    const res = await fetch('/api/inventory/sync');
    if (!res.ok) {
      return { updatedItems: currentItems, activeOrdersCount: 0, synced: false };
    }

    const data = await res.json();
    if (!data || (data.status !== 'synced' && data.status !== 'offline_or_unconfigured')) {
      return { updatedItems: currentItems, activeOrdersCount: data.activeOrdersCount || 0, synced: false };
    }

    const transitoByDash = data.enTransitoByDashboardId || {};
    const transitoByName = data.enTransitoByName || {};
    const liveStockByDash = data.liveStockByDashboardId || {};
    const liveCostByDash = data.liveCostByDashboardId || {};

    let updated: InventoryItem[] = currentItems.map(item => {
      // 1. Prioridad Tránsito OAB
      let transit = transitoByDash[item.id];
      if (transit === undefined && item.nombre) {
        transit = transitoByName[item.nombre.toLowerCase().trim()];
      }
      const enTransito = transit !== undefined ? transit : (item.enTransitoOAB || 0);

      // 2. Stock Base en Vivo (Ajustes de inventario y Despachos en tiempo real)
      let baseStock = item.stockBase || 0;
      let estado = item.estadoStock;
      const isMTO = (item.stockMinimo || 0) === 0 || item.prioridad === 'Por Pedido' || item.estadoStock === 'Por Pedido';

      if (liveStockByDash[item.id] !== undefined) {
        const liveVal = Number(liveStockByDash[item.id]);
        if (!isNaN(liveVal)) {
          baseStock = liveVal;
          if (isMTO) {
            estado = baseStock > 0 ? 'En Stock' : 'Por Pedido';
          } else if (baseStock === 0) {
            estado = 'Sin Stock';
          } else if (baseStock < (item.stockMinimo || 0)) {
            estado = 'Bajo Mínimo';
          } else {
            estado = 'En Stock';
          }
          saveStockOverride(item.id, baseStock, estado);
        }
      } else if (isMTO && estado !== 'Descontinuado' && estado !== 'En Reconteo') {
        estado = baseStock > 0 ? 'En Stock' : 'Por Pedido';
      }

      // Normalización de prioridades legadas
      let prio = item.prioridad || (isMTO ? 'Por Pedido' : 'Media');
      if (prio === 'Nivel 1') prio = 'Alta';
      else if (prio === 'Nivel 2') prio = 'Media';

      // 3. Costo Unitario en Vivo
      let unitCost = item.costoUnitarioUSD || 0;
      if (liveCostByDash[item.id] !== undefined && Number(liveCostByDash[item.id]) > 0) {
        unitCost = Number(liveCostByDash[item.id]);
        saveCostOverride(item.id, unitCost);
      }

      const stockProyectado = baseStock + enTransito;
      const deficit = isMTO ? 0 : Math.max(0, (item.stockMinimo || 0) - baseStock);

      let estadoColor = item.estadoStockColor;
      if (estado === 'Por Pedido') estadoColor = 'blue';
      else if (estado === 'Sin Stock') estadoColor = 'red';
      else if (estado === 'Bajo Mínimo') estadoColor = 'orange';
      else if (estado === 'En Stock') estadoColor = 'green';

      return {
        ...item,
        stockBase: baseStock,
        costoUnitarioUSD: unitCost,
        estadoStock: estado,
        estadoStockColor: estadoColor,
        prioridad: prio,
        deficit,
        enTransitoOAB: enTransito,
        stockProyectado,
        isOptimisticSync: false,
        syncNote: undefined
      };
    });

    // 4. Hidratación en caliente de adiciones del Catálogo Maestro desde Edge KV (Dual-Truth Loopback)
    const liveAdditions = Array.isArray(data.liveCatalogAdditions) ? data.liveCatalogAdditions : [];
    for (const liveItem of liveAdditions) {
      const liveId = liveItem.dashboardId || liveItem.id || liveItem.insumoId;
      const isStorable = liveItem.isStorable !== false && liveItem.contando !== false;

      const existingIndex = updated.findIndex(it => 
        (it.id && (it.id === liveId || it.id === liveItem.dashboardId || it.id === liveItem.insumoId)) ||
        (it.insumoId && (it.insumoId === liveItem.insumoId || it.insumoId === liveId)) ||
        (it.codigo && liveItem.codigo && it.codigo.toUpperCase() === liveItem.codigo.toUpperCase())
      );

      if (existingIndex !== -1) {
        if (!isStorable) {
          // Evicción reactiva si fue desmarcado de Almacenable
          updated.splice(existingIndex, 1);
        } else {
          // Actualizar campos en caliente si cambiaron
          const existing = updated[existingIndex];
          existing.isStorable = true;
          existing.contando = true;
          if (liveItem.nombre) existing.nombre = liveItem.nombre;
          if (liveItem.codigo) existing.codigo = liveItem.codigo;
          if (typeof liveItem.costoUnitarioUSD === 'number' && liveItem.costoUnitarioUSD > 0) {
            existing.costoUnitarioUSD = liveItem.costoUnitarioUSD;
          }
          if (liveItem.ubicacion !== undefined) existing.ubicacion = liveItem.ubicacion;
        }
      } else if (isStorable && liveItem.nombre) {
        const baseStk = liveItem.stockBase || 0;
        const minStk = liveItem.stockMinimo || 0;
        const isLiveMTO = minStk === 0 || liveItem.prioridad === 'Por Pedido';
        const def = isLiveMTO ? 0 : Math.max(0, minStk - baseStk);
        let livePrio = liveItem.prioridad || (isLiveMTO ? 'Por Pedido' : 'Media');
        if (livePrio === 'Nivel 1') livePrio = 'Alta';
        else if (livePrio === 'Nivel 2') livePrio = 'Media';

        const estStk = liveItem.estadoStock || (isLiveMTO ? (baseStk > 0 ? 'En Stock' : 'Por Pedido') : (baseStk === 0 ? 'Sin Stock' : (baseStk < minStk ? 'Bajo Mínimo' : 'En Stock')));
        let estStkColor = 'green';
        if (estStk === 'Por Pedido') estStkColor = 'blue';
        else if (estStk === 'Sin Stock') estStkColor = 'red';
        else if (estStk === 'Bajo Mínimo') estStkColor = 'orange';

        let livePrioColor = 'yellow';
        if (livePrio === 'Urgente') livePrioColor = 'red';
        else if (livePrio === 'Alta') livePrioColor = 'orange';
        else if (livePrio === 'Baja') livePrioColor = 'green';
        else if (livePrio === 'Por Pedido') livePrioColor = 'blue';

        const mappedItem: InventoryItem = {
          id: liveItem.dashboardId || liveItem.id || `live-${Date.now()}`,
          insumoId: liveItem.insumoId || liveItem.id,
          nombre: liveItem.nombre,
          codigo: liveItem.codigo || '',
          marca: liveItem.marca || '',
          stockBase: baseStk,
          stockMinimo: minStk,
          deficit: def,
          estadoStock: estStk,
          estadoStockColor: estStkColor,
          prioridad: livePrio,
          prioridadColor: livePrioColor,
          categoriaMaterial: liveItem.categoria || 'General',
          rolMaterial: liveItem.rolMaterial || 'Materia Prima',
          unidad: liveItem.unidad || 'UND',
          costoUnitarioUSD: liveItem.costoUnitarioUSD || 0,
          color: liveItem.color || '',
          dimensiones: liveItem.dimensiones || '',
          enTransitoOAB: 0,
          stockProyectado: baseStk,
          stockApartado: 0,
          stockLibre: baseStk,
          contando: true,
          isStorable: true,
          ubicacion: liveItem.ubicacion || '',
          isOptimisticSync: false
        };

        updated.unshift(mappedItem);
      }
    }

    updated = updated.filter(it => it.isStorable !== false && it.contando !== false);

    return {
      updatedItems: updated,
      activeOrdersCount: data.activeOrdersCount || 0,
      synced: true
    };
  } catch (err) {
    console.warn('Revalidación SWR en segundo plano no disponible:', err);
    return { updatedItems: currentItems, activeOrdersCount: 0, synced: false };
  }
}

// =========================================================================
// GOBIERNO DE ALMACÉN: RESERVAS MTO Y ASIGNACIONES MULTITIENDA (FASE 10C)
// =========================================================================

export interface StoreAllocation {
  id: string;
  dashboardId: string;
  insumoId: string;
  insumoNombre: string;
  materialNombre?: string;
  codigo: string;
  pedidoId?: string;
  pedidoCodigo?: string;
  proyectoId: string;
  proyectoCodigo?: string;
  proyectoNombre: string;
  cantidadApartada: number;
  cantidadTransito: number;
  cantidadConsumida: number;
  unidad?: string;
  costoUnitarioUSD?: number;
  folioOAB?: string;
  desgloseBultos?: number[];
  bultos?: number;
  cantEnBulto?: number;
  estado?: 'ACTIVA' | 'CONCLUIDA';
  archived?: boolean;
  updatedAt: number;
}

export interface ProjectSummary {
  proyectoId: string;
  pedidoId?: string;
  pedidoCodigo?: string;
  proyectoCodigo?: string;
  proyectoNombre: string;
  totalItems: number;
  totalApartado?: number;
  totalTransito?: number;
  totalUSD: number;
  items: StoreAllocation[];
}

export interface InsumoSummary {
  canonicalInsumoId?: string;
  totalApartado: number;
  totalTransito: number;
  totalConsumido: number;
  proyectosCount: number;
  desglose: StoreAllocation[];
}

export interface AllocationsResponse {
  status: string;
  allocations: StoreAllocation[];
  debts: any[];
  summaryByDashboardId: Record<string, InsumoSummary>;
  summaryByCodigo?: Record<string, InsumoSummary>;
  summaryByProyectoId: Record<string, ProjectSummary>;
  activeDebtsCount: number;
  timestamp: number;
}

/**
 * Resuelve el resumen MTO de un insumo consultando identidad multi-clave (PLAN-057):
 * 1) `item.id` -> 2) `item.insumoId` -> 3) `item.dashboardId` -> 4) `sku:<CODIGO>`
 */
export function getInsumoAllocationSummaryFromMap(
  summaryByDashboardId: Record<string, InsumoSummary> | undefined,
  item: { id?: string; insumoId?: string; dashboardId?: string; codigo?: string } | null | undefined
): InsumoSummary | undefined {
  if (!summaryByDashboardId || !item) return undefined;
  if (item.id && summaryByDashboardId[item.id]) return summaryByDashboardId[item.id];
  if (item.insumoId && summaryByDashboardId[item.insumoId]) return summaryByDashboardId[item.insumoId];
  if (item.dashboardId && summaryByDashboardId[item.dashboardId]) return summaryByDashboardId[item.dashboardId];
  const sku = item.codigo?.trim().toUpperCase();
  if (sku && summaryByDashboardId[`sku:${sku}`]) return summaryByDashboardId[`sku:${sku}`];
  return undefined;
}

export function getInsumoAllocationSummary(
  allocationsData: AllocationsResponse | null | undefined,
  item: { id?: string; insumoId?: string; dashboardId?: string; codigo?: string } | null | undefined
): InsumoSummary | undefined {
  if (!allocationsData || !item) return undefined;
  const fromMap = getInsumoAllocationSummaryFromMap(allocationsData.summaryByDashboardId, item);
  if (fromMap) return fromMap;
  const sku = item.codigo?.trim().toUpperCase();
  if (sku && allocationsData.summaryByCodigo?.[sku]) {
    return allocationsData.summaryByCodigo[sku];
  }
  return undefined;
}

/**
 * Construye cabeceras Zero-Trust inyectando el Bearer JWT activo de sesión (PLAN-057 / INC-013).
 */
export function buildMtoAuthHeaders(explicitToken?: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  let tok = explicitToken || '';
  if (!tok && typeof window !== 'undefined') {
    tok = sessionStorage.getItem('sanesca_auth_jwt') || localStorage.getItem('sanesca_auth_jwt') || '';
  }
  if (tok) {
    headers['Authorization'] = `Bearer ${tok}`;
  }
  return headers;
}

export async function fetchLiveAllocations(filter?: { dashboardId?: string; proyectoId?: string }): Promise<AllocationsResponse> {
  const params = new URLSearchParams();
  if (filter?.dashboardId) params.set('dashboardId', filter.dashboardId);
  if (filter?.proyectoId) params.set('proyectoId', filter.proyectoId);

  const res = await fetch(`/api/inventory/allocations?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Error consultando asignaciones: ${res.statusText}`);
  }
  return res.json();
}

export async function reserveStockDirect(payload: {
  dashboardId: string;
  insumoId?: string;
  materialNombre: string;
  codigo?: string;
  pedidoId?: string;
  pedidoCodigo?: string;
  proyectoId: string;
  proyectoCodigo?: string;
  proyectoNombre: string;
  cantidad: number;
  unidad?: string;
  costoUnitarioUSD?: number;
  notas?: string;
  token?: string | null;
  supervisorPIN?: string;
  supervisorEmployeeId?: string;
}) {
  const { token, ...rest } = payload;
  const res = await fetch('/api/inventory/allocations', {
    method: 'POST',
    headers: buildMtoAuthHeaders(token),
    body: JSON.stringify({
      action: 'reserve',
      ...rest
    })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(err.error || `Error reservando material (${res.status})`);
  }
  return res.json();
}

export async function reassignStoreStock(payload: {
  allocId?: string;
  dashboardId: string;
  insumoId?: string;
  codigo?: string;
  materialNombre: string;
  origenProyectoId: string;
  origenPedidoId?: string;
  origenProyectoNombre: string;
  destinoProyectoId: string;
  destinoProyectoNombre: string;
  destinoPedidoId?: string;
  destinoPedidoCodigo?: string;
  cantidad: number;
  motivo: string;
  reponerCedente?: boolean;
  unidad?: string;
  token?: string | null;
  supervisorPIN?: string;
  supervisorEmployeeId?: string;
}) {
  const { token, ...rest } = payload;
  const res = await fetch('/api/inventory/allocations', {
    method: 'POST',
    headers: buildMtoAuthHeaders(token),
    body: JSON.stringify({
      action: 'reassign',
      ...rest
    })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(err.error || `Error reasignando material (${res.status})`);
  }
  return res.json();
}

export async function releaseStoreStock(payload: {
  allocId?: string;
  dashboardId: string;
  insumoId?: string;
  codigo?: string;
  materialNombre: string;
  proyectoId: string;
  pedidoId?: string;
  proyectoNombre: string;
  cantidad: number;
  motivo?: string;
  unidad?: string;
  token?: string | null;
  supervisorPIN?: string;
  supervisorEmployeeId?: string;
}) {
  const { token, ...rest } = payload;
  const res = await fetch('/api/inventory/allocations', {
    method: 'POST',
    headers: buildMtoAuthHeaders(token),
    body: JSON.stringify({
      action: 'release',
      ...rest
    })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(err.error || `Error liberando material (${res.status})`);
  }
  return res.json();
}

export async function liquidateStoreAllocations(payload: {
  proyectoId: string;
  pedidoId?: string;
  proyectoNombre?: string;
  motivo?: string;
  itemsToRelease?: Array<{
    allocId?: string;
    dashboardId: string;
    insumoId?: string;
    codigo?: string;
    materialNombre?: string;
    cantidadLiberar: number;
  }>;
  marcarProyectoConcluido?: boolean;
  token?: string | null;
  supervisorPIN?: string;
  supervisorEmployeeId?: string;
}) {
  const { token, ...rest } = payload;
  const res = await fetch('/api/inventory/allocations', {
    method: 'POST',
    headers: buildMtoAuthHeaders(token),
    body: JSON.stringify({
      action: 'liquidate',
      ...rest
    })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(err.error || `Error liquidando obra (${res.status})`);
  }
  return res.json();
}

export async function reconcileAllocations(
  applyFix: boolean = false,
  token?: string | null,
  supervisorPIN?: string,
  options?: {
    autoTruncateOverReservations?: boolean;
    supervisorEmployeeId?: string;
  }
) {
  const headers = buildMtoAuthHeaders(token);
  const bodyPayload: Record<string, any> = {
    applyFix,
    autoTruncateOverReservations: Boolean(options?.autoTruncateOverReservations)
  };
  if (supervisorPIN) bodyPayload.supervisorPIN = supervisorPIN;
  if (options?.supervisorEmployeeId) bodyPayload.supervisorEmployeeId = options.supervisorEmployeeId;

  const res = await fetch('/api/inventory/reconcile', {
    method: 'POST',
    headers,
    body: JSON.stringify(bodyPayload)
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Error desconocido' }));
    throw new Error(err.error || `Error ejecutando reconciliación (${res.status})`);
  }

  return res.json();
}

/**
 * Invalida de forma atómica la caché de Edge KV y sincroniza datos en caliente (PLAN-013 - Decisión 3)
 */
export async function forceRevalidateNotion(): Promise<{ ok: boolean; message?: string }> {
  try {
    const res = await fetch('/api/inventory/revalidate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    return { ok: true, message: data.message };
  } catch (err: any) {
    console.warn('Error revalidando inventario en caliente:', err);
    return { ok: false, message: err.message || 'Error de conexión al revalidar' };
  }
}



